import React, { createContext, useContext, useState, useEffect } from 'react';

// Language context for managing bilingual support
const LanguageContext = createContext();

// Language data with Marathi (default) and English translations
const languageData = {
  marathi: {
    // Navigation
    home: 'मुख्यपृष्ठ',
    about: 'आमच्याविषयी',
    aboutDropdown: {
      overview: 'आढावा',
      administrativeStructure: 'प्रशासकिय रचना',
      boardProject: 'मंडळ प्रकल्प',
      boardMembers: 'मंडळ सदस्य',
      annualReport: 'वार्षिक गोषवारा',
      circulars: 'कर्मचाऱ्यांसाठी परिपत्रके'
    },
    workersWelfare: 'कामगार कल्याण निधी',
    schemes: 'योजना',
    initiatives: 'उपक्रम',
    programs: 'कार्यक्रम',
    awards: 'पुरस्कार',
    rightToInfo: 'माहितीचा अधिकार',
    rightToService: 'सेवेचा अधिकार',
    resources: 'आरटीआय / आरटीएस',
    tendersNotices: 'निविदा व सूचना',
    eTender: 'विभागीय निविदा',
    mahaTender: 'महाटेंडर्स',
    gemPortal: 'जीईएम पोर्टल',
    mediaGallery: 'मीडिया गॅलरी',
    photoGallery: 'फोटो गॅलरी',
    videoGallery: 'व्हिडिओ गॅलरी',
    services: 'सेवा',
    servicesDropdown: {
      listOfServices: 'सेवांची यादी',
      applyHere: 'सेवांसाठी अर्ज करा',
      feedback: 'अभिप्राय',
      grievance: 'तक्रार नोंदवा',
      faq: 'वारंवार विचारले जाणारे प्रश्न',
      dashboard: 'डॅशबोर्ड',
      publicPortal: 'पब्लिक पोर्टल',
      aapleSarkar: 'आपले सरकार'
    },
    contact: 'संपर्क',
    hundredDaysProgram: '१०० दिवसांचा कार्यक्रम',
    
    // Common elements
    search: 'शोधा',
    language: 'भाषा',
    accessibility: 'सुलभता',
    increaseFont: 'फॉन्ट वाढवा',
    decreaseFont: 'फॉन्ट कमी करा',
    resetFont: 'फॉन्ट रीसेट करा',
    theme: 'थीम',
    defaultTheme: 'डिफॉल्ट',
    grayscaleTheme: 'काळा-पांढरा',
    
    // Footer
    visitorCount: 'एकूण भेटी',
    lastUpdated: 'शेवटचे अपडेट',
    
    // Placeholder content
    comingSoon: 'लवकरच येणार...',
    loading: 'लोड होत आहे...',
    error: 'त्रुटी आली',
    retry: 'पुन्हा प्रयत्न करा',
    
    // Quick Links
    quickLinks: 'त्वरित लिंक्स',
    actMarathi: 'महाराष्ट्र कामगार कल्याण निधी अधिनियम १९५३ (सर्व बदलासह मराठी आवृत्ती)',
    actEnglish: 'महाराष्ट्र कामगार कल्याण निधी अधिनियम १९५३ (इंग्रजी आवृत्ती)',
    notification: 'अधिसूचना',
    annualReport: 'वार्षिक गोषवारा',
    citizensCharter: 'नागरिकांची सनद',
    
    // Important Circulars
    importantCirculars: 'महत्वाची परिपत्रके',
    groundReservation: 'मैदान आरक्षणाबाबत',
    variousFees: 'विविध सोयी-सुविधांचे शुल्क',
    timeboundProgram: 'कालबद्ध कार्यक्रम',
    nationalDayCelebration: 'राष्ट्रीय दिन साजरा करणेबाबत',
    
    // Dashboard
    dashboardTitle: 'डॅशबोर्ड',
    dashboardSubtitle: 'जिल्हावार आकडेवारीचा सर्वसमावेशक आढावा',
    districtWiseStatistics: 'जिल्हावार आकडेवारी',
    memberAndFamilyData: 'सदस्य आणि कुटुंब डेटा जिल्ह्यानुसार',
    districtWiseComparison: 'जिल्हावार तुलना',
    memberAndFamilyStats: 'सदस्य आणि कुटुंब आकडेवारी जिल्ह्यांमध्ये',
    members: 'सदस्य',
    families: 'कुटुंबे',
    others: 'इतर',
    total: 'एकूण',
    unknownDistrict: 'अज्ञात जिल्हा',
    unknownBranch: 'अज्ञात शाखा',
    noDataAvailable: 'डेटा उपलब्ध नाही',
    unableToFetchData: 'सध्या जिल्हा आकडेवारी मिळू शकत नाही. कृपया नंतर पुन्हा प्रयत्न करा.',
    loadingData: 'डेटा लोड होत आहे...',
    boardsWork: 'मंडळाचे कार्य'
  },
  english: {
    // Navigation
    home: 'Home',
    about: 'About Us',
    aboutDropdown: {
      overview: 'Overview',
      administrativeStructure: 'Administrative Structure',
      boardProject: 'Board Project',
      boardMembers: 'Board Members',
      annualReport: 'Annual Report',
      circulars: 'Circulars for Employees'
    },
    workersWelfare: 'Labours\' Welfare Fund',
    schemes: 'Schemes',
    initiatives: 'Activities',
    programs: 'Programs',
    awards: 'Awards',
    rightToInfo: 'Right to Information',
    rightToService: 'Right To Service',
    resources: 'RTI / RTS',
    tendersNotices: 'Tenders & Notices',
    eTender: 'Departmental Tenders',
    mahaTender: 'Mahatenders',
    gemPortal: 'GeM Portal',
    mediaGallery: 'Media Gallery',
    photoGallery: 'Photo Gallery',
    videoGallery: 'Video Gallery',
    services: 'Services',
    servicesDropdown: {
      listOfServices: 'List of Services',
      applyHere: 'Apply to Services',
      feedback: 'Feedback',
      grievance: 'Register Your Grievance',
      faq: 'FAQ',
      dashboard: 'Dashboard',
      publicPortal: 'Public Portal',
      aapleSarkar: 'Aaple Sarkar'
    },
    contact: 'Contact Us',
    hundredDaysProgram: '100 Days Program',
    
    // Common elements
    search: 'Search',
    language: 'Language',
    accessibility: 'Accessibility',
    increaseFont: 'Increase Font',
    decreaseFont: 'Decrease Font',
    resetFont: 'Reset Font',
    theme: 'Theme',
    defaultTheme: 'Default',
    grayscaleTheme: 'Black & White',
    
    // Footer
    visitorCount: 'Total Visitors',
    lastUpdated: 'Last Updated',
    
    // Placeholder content
    comingSoon: 'Coming Soon...',
    loading: 'Loading...',
    error: 'Error occurred',
    retry: 'Retry',
    
    // Quick Links
    quickLinks: 'Quick Links',
    actMarathi: 'Maharashtra Labour Welfare Fund Act 1953 (Marathi Version with all amendments)',
    actEnglish: 'Maharashtra Labour Welfare Fund Act 1953 (English Version)',
    notification: 'Notification',
    annualReport: 'Annual Report',
    citizensCharter: 'Citizens Charter',
    
    // Important Circulars
    importantCirculars: 'Important Circulars',
    groundReservation: 'Regarding Ground Reservation',
    variousFees: 'Fees for Various Facilities',
    timeboundProgram: 'Time-bound Program',
    nationalDayCelebration: 'Regarding National Day Celebration',
    
    // Dashboard
    dashboardTitle: 'Dashboard',
    dashboardSubtitle: 'Comprehensive Overview of District-wise Statistics',
    districtWiseStatistics: 'District-wise Statistics',
    memberAndFamilyData: 'Member and Family Data by District',
    districtWiseComparison: 'District-wise Comparison',
    memberAndFamilyStats: 'Member and Family Statistics Across Districts',
    members: 'Members',
    families: 'Families',
    others: 'Others',
    total: 'Total',
    unknownDistrict: 'Unknown District',
    unknownBranch: 'Unknown Branch',
    noDataAvailable: 'No Data Available',
    unableToFetchData: 'Unable to fetch district statistics at the moment. Please try again later.',
    loadingData: 'Loading data...',
    boardsWork: 'Board\'s Work'
  }
};

// Language provider component
export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('marathi');
  const [translations, setTranslations] = useState(languageData.marathi);

  // Load saved language preference from localStorage
  useEffect(() => {
    const savedLanguage = localStorage.getItem('mlwb-language');
    if (savedLanguage && languageData[savedLanguage]) {
      setCurrentLanguage(savedLanguage);
      setTranslations(languageData[savedLanguage]);
    }
  }, []);

  // Update document language attribute for accessibility
  useEffect(() => {
    document.documentElement.lang = currentLanguage === 'marathi' ? 'mr' : 'en';
  }, [currentLanguage]);

  // Update document title based on language
  useEffect(() => {
    document.title = currentLanguage === 'marathi' 
      ? 'महाराष्ट्र कामगार कल्याण मंडळ'
      : 'Maharashtra Labour Welfare Board';
  }, [currentLanguage]);

  // Function to toggle language
  const toggleLanguage = () => {
    const newLanguage = currentLanguage === 'marathi' ? 'english' : 'marathi';
    setCurrentLanguage(newLanguage);
    setTranslations(languageData[newLanguage]);
    localStorage.setItem('mlwb-language', newLanguage);
  };

  // Function to get translation
  const t = (key) => {
    return translations[key] || key;
  };

  // Function to get current language
  const getCurrentLanguage = () => {
    return currentLanguage;
  };

  // Function to check if current language is Marathi
  const isMarathi = () => {
    return currentLanguage === 'marathi';
  };

  const value = {
    currentLanguage,
    translations,
    toggleLanguage,
    t,
    getCurrentLanguage,
    isMarathi
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
